'use client';

import { useEffect, useState } from 'react';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { createClient } from '@/lib/supabase/browser';

type Status = {
  suspended_until: string | null;
  suspended_reason: string | null;
} | null;

export default function SuspensionBanner() {
  const { user, loading } = useAuthContext();
  const [status, setStatus] = useState<Status>(null);

  useEffect(() => {
    if (loading || !user?.id) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const supabase = createClient();
      const { data } = await supabase
        .from('profiles')
        .select('suspended_at, suspended_until, suspended_reason')
        .eq('id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (data?.suspended_at) {
        const stillSuspended =
          !data.suspended_until || new Date(data.suspended_until) > new Date();
        setStatus(
          stillSuspended
            ? {
                suspended_until: data.suspended_until,
                suspended_reason: data.suspended_reason,
              }
            : null
        );
      } else {
        setStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, user?.id]);

  if (!status) return null;

  const untilLabel = status.suspended_until
    ? `until ${new Date(status.suspended_until).toLocaleDateString()}`
    : 'indefinitely';

  return (
    <div className="bg-status-busy/10 border-b border-status-busy/40 px-4 py-2">
      <div className="max-w-7xl mx-auto text-meta text-status-busy">
        Your account is suspended {untilLabel}
        {status.suspended_reason ? ` for: ${status.suspended_reason}` : ''}. Contact{' '}
        <a
          href="mailto:support@indiestryvoice.com"
          className="underline hover:no-underline"
        >
          support@indiestryvoice.com
        </a>{' '}
        to appeal.
      </div>
    </div>
  );
}
